import _ from 'lodash';
import { TBinaryUpdate } from 'shared/types/domain';

import { createEditor } from 'com/Editor';
import { apiAddWord, apiGetDeck, apiGetWord, apiUpdateWord } from 'core/api';
import { checkWordTemplate } from 'core/helpers/text';

import Form from './Form';

const editor = createEditor<TDeck, TWord, TWordUpdate>({
  mode: 'withContainer',
  transform,
  Form,
  getParentUid: entity => entity.deckUid,
  onValidate: validate,
  genNewEntity: (): TWordUpdate => ({
    definitions: {},
    note: null,
    value: '',
    valueNote: null,
    image: null,
    tags: [],
  }),
  apiGetContainer: apiGetDeck,
  apiGetEntity: apiGetWord,
  apiUpdateEntity: apiUpdateWord,
  apiAddEntity: apiAddWord,
});

function validate(word: TWordUpdate | TWord) {
  const hasNoDefinitions =
    _.isEmpty(word.definitions) &&
    Object.values(word.definitions).every(value => !value.trim());

  if (hasNoDefinitions && !word.value) {
    return { isEmpty: true, error: null };
  }

  if (!word.value) {
    return { isEmpty: false, error: 'Value is empty' };
  }

  if (hasNoDefinitions) {
    return {
      isEmpty: false,
      error: 'The card must contain at least 1 definition',
    };
  }

  const template = [
    word.value,
    ...Object.values(word.definitions).filter(Boolean),
  ].find(raw => raw && !checkWordTemplate(raw));

  if (template) {
    return { isEmpty: false, error: `Template "${template}" is not correct` };
  }

  return { isEmpty: false, error: null };
}

function transform(
  word: TWord | TWordUpdate,
  field: string,
  subField: string | undefined,
  value: string
): TWordUpdate {
  switch (field) {
    case 'definitions':
      return {
        ...word,
        definitions: {
          ...word.definitions,
          [subField as TLang]: value ? value : null,
        },
      };

    case 'valueResourceBinary':
    case 'definitionResourceBinary': {
      return {
        ...word,
        [field]: value ? (JSON.parse(value) as TBinaryUpdate) : null,
      };
    }

    default:
      // plain field like "note", "valueNote"
      return { ...word, [field as keyof TWord]: value ? value : null };
  }
}

export const WordEditorProvider = editor.EditorProvider;
export const useWordEditor = function useWordEditor() {
  const { createEntity, editEntity } = editor.useEditor();
  return { createWord: createEntity, editWord: editEntity };
};
